import React, { useState, useEffect, useContext } from "react";
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import { FormControl, InputLabel, Input, Button, Select, MenuItem, Alert, TextField, Box, IconButton } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import CircularProgress from '@mui/material/CircularProgress';

import APIForm from "./ConnectionAPIForm";
import NotSupportedForm from "./ConnectionNotSupportedForm";
import DbForm from "./ConnectionDBForm";
import EmailForm from "./ConnectionEmailForm";

import { useAppSnackbar } from '../../contexts/AppSnackbarContext';
import { useAppConfirm } from '../../contexts/AppConfirmContext';

const ConnectionDirectionDictionary = [
    "Inbound",
    "Outbound"
];

const ConnectionTypeDictionary = [
    "Database",
    "API",
    "SFTP",
    "Email",
];

export default function ConnectionForm() {
    const [validationError, setValidationError] = useState({});
    const [isLoading, setIsLoading] = useState(false)
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const { connectionId } = useParams();
    const [connectionForm, setConnectionForm] = useState({ name: '', type: null, direction: null });
    const { setRefreshTree } = useOutletContext();
    const navigate = useNavigate();
    const [showError, setShowError] = useState(false);
    const [showSubmit, setShowSubmit] = useState(true);
    const [id, setId] = useState(null);

    // Edit mode and global snackbar/confirm
    const [editMode, setEditMode] = useState(connectionId ? false : true);
    const { showSnackbar } = useAppSnackbar();
    const { confirm } = useAppConfirm();

    // Initial Effect
    useEffect(() => {
        setEditMode(connectionId ? false : true);
        let isMounted = true;

        async function fetchData() {
            setIsLoading(true);
            // Fetch connection needed
            if (connectionId) {
                const resp = await fetch(`/api/connection/${connectionId}`, { credentials: 'include' });
                let conn = await resp.json();
                if (isMounted) setConnectionForm(conn);
            }
            setId(connectionId ?? "new");
            if (!connectionId) {
                setConnectionForm({ name: '', type: null, direction: null });
            }
            if (isMounted) setIsLoading(false);
        }
        fetchData();
        return () => { isMounted = false; };
    }, [connectionId]);

    // Handlers
    const handleEdit = () => setEditMode(true);

    const handleCancel = () => {
        if (connectionId) {
            setEditMode(false);
            setIsLoading(true);
            fetch(`/api/connection/${connectionId}`, { credentials: 'include' })
                .then(resp => resp.json())
                .then(conn => setConnectionForm(conn))
                .finally(() => setIsLoading(false));
        } else {
            navigate('/connections');
        }
    };

    const handleDelete = async () => {
        if (!connectionId) return;
        const confirmed = await confirm({
            title: "Delete Connection",
            content: 'Are you sure you want to Delete? This process will be permanent',
            confirmText: 'Accept',
            cancelText: 'Cancel'
        });
        if (!confirmed) return;
        setLoadingSubmit(true);
        try {
            const resp = await fetch(`/api/connection/${connectionId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            if (resp.ok) {
                showSnackbar("Connection deleted successfully.", "success");
                setRefreshTree(prev => prev + 1);
                navigate('/connections', { replace: true });
            } else {
                const msg = await resp.text();
                showSnackbar(msg || 'Failed to delete connection.', "error");
            }
        } catch (err) {
            showSnackbar('Error deleting connection.', "error");
        } finally {
            setLoadingSubmit(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (Object.values(validationError).some(error => error != null)) {
            setShowError(true);
            return;
        }
        if (!connectionForm.name || connectionForm.name.trim() === '') {
            setValidationError(prev => ({ ...prev, [`name-${id}`]: 'Name is required' }));
            setShowError(true);
            return;
        } else {
            setValidationError(prev => ({ ...prev, [`name-${id}`]: null }));
        }
        if (connectionForm.type == null || connectionForm.type === -1) {
            setValidationError(prev => ({ ...prev, [`connection-type-select-${id}`]: 'Type is required' }));
            setShowError(true);
            return;
        } else {
            setValidationError(prev => ({ ...prev, [`connection-type-select-${id}`]: null }));
        }
        if (connectionForm.direction == null || connectionForm.direction === -1) {
            setValidationError(prev => ({ ...prev, [`connection-direction-select-${id}`]: 'Direction is required' }));
            setShowError(true);
            return;
        } else {
            setValidationError(prev => ({ ...prev, [`connection-direction-select-${id}`]: null }));
        }
        if (connectionId)
            updateConnection(connectionForm);
        else
            createConnection(connectionForm)
    }

    async function createConnection(connection) {
        try {
            setLoadingSubmit(true);
            const response = await fetch('/api/connection', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(connection)
            });
            if (response.ok) {
                showSnackbar('Connection created successfully.', "success");
                setRefreshTree(prev => prev + 1);
                navigate('/connections');
            } else {
                const msg = await response.text();
                showSnackbar(msg || 'Error creating connection.', "error");
            }
        } catch (error) {
            showSnackbar('Error creating connection.', "error");
        } finally {
            setLoadingSubmit(false);
        }
    }

    async function updateConnection(connection) {
        try {
            setLoadingSubmit(true);
            const response = await fetch(`/api/connection/${connection.id}`, {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(connection)
            });
            if (response.ok) {
                showSnackbar('Connection updated successfully.', "success");
                setRefreshTree(prev => prev + 1);
                setEditMode(false);
            } else {
                const msg = await response.text();
                showSnackbar(msg || 'Error updating connection.', "error");
            }
        } catch (error) {
            showSnackbar('Error updating connection.', "error");
        } finally {
            setLoadingSubmit(false);
        }
    }

    // Field handlers
    const handleTypeChange = (event) => {
        if (!editMode) return;
        const newType = event.target.value;
        setConnectionForm(prev => ({ ...prev, type: newType }));
        setValidationError(prev => ({ ...prev, [`connection-type-select-${id}`]: null }));
        setShowError(false);
    }

    const handleNameChange = (event) => {
        if (!editMode) return;
        const newName = event.target.value;
        setConnectionForm(prev => ({ ...prev, name: newName }));
        setValidationError(prev => ({ ...prev, [`name-${id}`]: null }));
        setShowError(false);
    }

    const handleDirectionChange = (event) => {
        if (!editMode) return;
        const newDirection = event.target.value;
        setConnectionForm(prev => ({ ...prev, direction: newDirection }));
        setValidationError(prev => ({ ...prev, [`connection-direction-select-${id}`]: null }));
        setShowError(false);
    }

    const handleDataChange = (data) => {
        if (!editMode) return;
        setConnectionForm(prev => ({ ...prev, data: JSON.stringify(data) }));
        setShowError(false);
    }

    return (
        <div style={{ width: '100%', padding: 20 }}>
            {isLoading && (<CircularProgress />)}
            {showError && validationError && Object.keys(validationError).length > 0 && (
                <Alert severity="error" style={{ marginBottom: 20 }}>
                    {Object.values(validationError).map((error, index) => (
                        <div key={index}>{error}</div>
                    ))}
                </Alert>
            )}
            {!isLoading && (
                <>
                    <form onSubmit={handleSubmit}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                            <h2 style={{ margin: 0 }}>
                                {id === 'new' ? 'Create New Connection' : 'Connection Details'}
                            </h2>
                            {!editMode && id !== 'new' && (
                                <IconButton color="primary" onClick={handleEdit}>
                                    <EditIcon />
                                </IconButton>
                            )}
                            {editMode && (
                                <>
                                    <Box display="flex" gap={1} flexDirection="row">
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            startIcon={<SaveIcon />}
                                            disabled={loadingSubmit}
                                        >
                                            {id === 'new' ? 'Create Connection' : 'Save Changes'}
                                        </Button>
                                        {id !== 'new' && (
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                startIcon={<DeleteIcon />}
                                                onClick={handleDelete}
                                                disabled={loadingSubmit}
                                            >
                                                Delete
                                            </Button>
                                        )}
                                        <Button
                                            variant="outlined"
                                            startIcon={<CancelIcon />}
                                            onClick={handleCancel}
                                            disabled={loadingSubmit}
                                        >
                                            Cancel
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </Box>
                        <FormControl fullWidth margin="normal">
                            <TextField
                                error={validationError[`name-${id}`] != null}
                                helperText={validationError[`name-${id}`]}
                                id={`name-${id}`}
                                label="Connection Name"
                                onChange={handleNameChange}
                                variant={editMode ? "outlined" : "standard"}
                                name={`name-${id}`}
                                value={connectionForm.name}
                                InputProps={{
                                    readOnly: !editMode
                                }}
                            />
                        </FormControl>
                        {id === 'new' || editMode ? (
                            <>
                                <div>
                                    <FormControl fullWidth margin="normal">
                                        <InputLabel id={`connection-type-label-${id}`}>Type</InputLabel>
                                        <Select
                                            error={validationError[`connection-type-select-${id}`] != null}
                                            labelId={`connection-type-label-${id}`}
                                            id={`connection-type-select-${id}`}
                                            value={connectionForm.type ?? -1}
                                            label="Type"
                                            onChange={handleTypeChange}
                                            disabled={!editMode}
                                        >
                                            <MenuItem value={-1} disabled>Type</MenuItem>
                                            {ConnectionTypeDictionary.map((type, index) => <MenuItem key={type} value={index}>{type}</MenuItem>)}
                                            <MenuItem value={99}>Other</MenuItem>
                                        </Select>
                                    </FormControl>
                                </div>
                                <div>
                                    <FormControl fullWidth margin="normal">
                                        <InputLabel id={`connection-direction-label-${id}`}>Direction</InputLabel>
                                        <Select
                                            error={validationError[`connection-direction-select-${id}`] != null}
                                            labelId={`connection-direction-label-${id}`}
                                            id={`connection-direction-select-${id}`}
                                            value={connectionForm.direction ?? -1}
                                            label="Direction"
                                            onChange={handleDirectionChange}
                                            disabled={!editMode}
                                        >
                                            <MenuItem value={-1} disabled>Direction</MenuItem>
                                            {ConnectionDirectionDictionary.map((dir, index) => <MenuItem key={dir} value={index}>{dir}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </div>
                            </>
                        ) : (
                            <>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel htmlFor={`connection-type-${id}`}>Type</InputLabel>
                                    <Input id={`connection-type-${id}`} disabled name={`connection-type-${id}`} type="text" value={ConnectionTypeDictionary[connectionForm.type] ?? 'Other'} />
                                </FormControl>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel htmlFor={`connection-direction-${id}`}>Direction</InputLabel>
                                    <Input id={`connection-direction-${id}`} disabled name={`connection-direction-${id}`} type="text" value={ConnectionDirectionDictionary[connectionForm.direction]} />
                                </FormControl>
                            </>
                        )}
                        {connectionForm.type === 0
                            && (
                                <DbForm
                                    connection={connectionForm}
                                    onDataChange={handleDataChange}
                                    validationError={validationError}
                                    setValidationError={setValidationError}
                                    editMode={editMode}
                                />
                            )}
                        {connectionForm.type === 1 && (
                            <APIForm
                                connection={connectionForm}
                                onDataChange={handleDataChange}
                                validationError={validationError}
                                setValidationError={setValidationError}
                                setShowSubmit={setShowSubmit}
                                editMode={editMode}
                            />
                        )}
                        {connectionForm.type === 3 && (
                            <EmailForm
                                connection={connectionForm}
                                onDataChange={handleDataChange}
                                validationError={validationError}
                                setValidationError={setValidationError}
                                setShowSubmit={setShowSubmit}
                                editMode={editMode}
                            />
                        )}
                        {
                            connectionForm.type != null && ![0, 1, 3].includes(connectionForm.type) && (
                                <NotSupportedForm
                                    connection={connectionForm}
                                    onDataChange={handleDataChange}
                                    validationError={validationError}
                                    setValidationError={setValidationError}
                                    editMode={editMode}
                                />
                            )}
                    </form>
                </>
            )}
        </div>
    );
}
